import type { VectorSourceDescriptor } from '../data/model';
import type { LayerDefinition } from '../project/model';
import { evaluateAttributeRule } from '../rules/evaluate';
import type { AttributeRuleCondition, AttributeRuleGroup } from '../rules/model';
import { isAttributeRuleGroupValid, MAX_ATTRIBUTE_RULE_CHILDREN } from '../rules/validation';
import { selectedFeatureRef, type SelectedFeatureRef } from './model';

export const MAX_ATTRIBUTE_QUERY_CONDITIONS = MAX_ATTRIBUTE_RULE_CHILDREN;

export function isAttributeQueryValid(
  value: unknown,
  effectiveSource: VectorSourceDescriptor,
): value is AttributeRuleGroup {
  return (
    isAttributeRuleGroupValid(value, effectiveSource.fields) &&
    value.operator === 'and' &&
    value.children.length <= MAX_ATTRIBUTE_QUERY_CONDITIONS &&
    value.children.every((child): child is AttributeRuleCondition => child.kind === 'condition')
  );
}

/** Pure O(features x conditions) query over effective properties and canonical identity. */
export function matchingSelectableFeatures(
  layer: LayerDefinition,
  canonicalSource: VectorSourceDescriptor,
  effectiveSource: VectorSourceDescriptor,
  query: AttributeRuleGroup,
): readonly SelectedFeatureRef[] | null {
  if (
    layer.sourceId !== canonicalSource.id ||
    effectiveSource.id !== canonicalSource.id ||
    effectiveSource.data.features.length !== canonicalSource.data.features.length ||
    !isAttributeQueryValid(query, effectiveSource)
  ) {
    return null;
  }

  const matches: SelectedFeatureRef[] = [];
  for (let index = 0; index < effectiveSource.data.features.length; index += 1) {
    const effective = effectiveSource.data.features[index];
    const canonical = canonicalSource.data.features[index];
    if (
      effective &&
      canonical &&
      evaluateAttributeRule(query, effective.properties) &&
      evaluateAttributeRule(layer.displayFilter, effective.properties)
    ) {
      matches.push(selectedFeatureRef(layer.id, canonicalSource.id, canonical, index));
    }
  }
  return matches;
}
